import { useCallback, useMemo } from 'preact/hooks';
import DiscoverPromotionsDrawer from '@/widgets/ProductDetailsHero/drawers/DiscoverPromotionsDrawer/DiscoverPromotionsDrawer.tsx';
import Action from '@/widgets/ProductDetailsHero/parts/Action/Action.tsx';
import { hasPromotions, type Product } from '@/domain/Product.ts';
import { getLowestPricedPromotion } from '@/domain/Promotion.ts';
import type { PersistedState } from '@/widgets/ProductDetailsHero/Store.ts';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import useTranslations from '@/widgets/hooks/useTranslations';
import type {
  Options,
  Translations
} from '@/widgets/ProductDetailsHero/config';
import useRoute from '@/widgets/hooks/useRoute.tsx';
import useMarketConfig from '@/widgets/hooks/useMarketConfig.tsx';
import { trackPromotionsModalOpened } from '@/widgets/ProductDetailsHero/Tracking.ts';
import styles from './PromotionAction.module.scss';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions';

export type PromotionActionProps = {
  product: Product;
};

export default function PromotionAction({ product }: PromotionActionProps) {
  const { t } = useTranslations<Translations>();
  const { locale } = useRoute();
  const { buyOptionsPlacement } = useWidgetOptions<Options>();
  const {
    financialSettings: { defaultCurrency: currencyCode }
  } = useMarketConfig();

  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();

  const closeDrawer = useCallback(() => {
    setPersistedState({
      openModal: undefined
    });
  }, [setPersistedState]);

  const openPromotionsOptionsModal = useCallback(() => {
    trackPromotionsModalOpened(`sku promo info show`, product);
    setPersistedState({ openModal: 'discover-promotions' });
  }, []);

  if (!hasPromotions(product) || !buyOptionsPlacement.includes('PROMOTIONS')) {
    return null;
  }

  const lowestPricedPromotion = useMemo(
    () => getLowestPricedPromotion(product),
    [product]
  );

  const promotionPriceLabel =
    lowestPricedPromotion !== undefined
      ? t('productDetailsHero.productVariantsSection.promotionFromPrice', {
          price: lowestPricedPromotion
        })
      : '';

  return (
    <div class={styles.container}>
      <Action onClick={openPromotionsOptionsModal} class={styles.button}>
        <Action.Icon icon="24/symbol/promotions" />
        <Action.IconLabel
          label={t(
            'productDetailsHero.productVariantsSection.promotionAvailable'
          )}
        />
        <Action.CtaLabel
          label={promotionPriceLabel}
          class={styles.priceLabel}
        />
        <Action.CtaIcon icon="24/symbol/chevron-right" />
      </Action>
      <DiscoverPromotionsDrawer
        isOpen={openModal.value === 'discover-promotions'}
        onClose={closeDrawer}
        product={product}
        currencyCode={currencyCode}
        locale={locale}
        trackingSource="sku"
      />
    </div>
  );
}
